// 图书库核心功能实现

import type {
  BookInfo,
  BookCollection,
  BookRating,
  UserBookData
} from '@/types/reading';

import type { StorageManager } from './storage';
import { BookSearchManager } from './bookSearchManager';

export class BookLibrary {
  private storage: StorageManager;
  private searchManager: BookSearchManager;
  private bookCache: Map<string, BookInfo> = new Map();

  constructor(storage: StorageManager, searchManager: BookSearchManager) {
    this.storage = storage;
    this.searchManager = searchManager;
  }

  // ============================================================================
  // 图书收藏管理
  // ============================================================================

  async addBookToLibrary(bookId: string): Promise<boolean> {
    try {
      // 1. 获取图书详情
      const bookInfo = await this.searchManager.getBookDetail(bookId);
      if (!bookInfo) {
        return false;
      }

      // 2. 检查是否已存在
      const existingBook = await this.storage.getBookInfo(bookId);
      if (existingBook) {
        return true; // 已存在，返回成功
      }

      // 3. 保存到图书库
      await this.storage.saveBookInfo(bookInfo);

      // 4. 创建用户图书数据
      const userBookData: UserBookData = {
        bookId,
        addedTime: new Date(),
        isFavorite: false,
        tags: [],
        notes: '',
        rating: undefined,
        readingProgress: {
          bookId,
          chapterId: bookInfo.chapters[0]?.id || '',
          position: 0,
          percentage: 0,
          lastReadTime: new Date(),
          totalReadTime: 0,
          currentChapterId: bookInfo.chapters[0]?.id || '',
          currentChapterIndex: 0,
          progress: 0,
          totalReadingTime: 0
        }
      };

      await this.storage.saveUserBookData(userBookData);

      // 5. 更新缓存
      this.bookCache.set(bookId, bookInfo);

      return true;

    } catch (error) {
      console.error('Add book to library failed:', error);
      return false;
    }
  }

  async removeBookFromLibrary(bookId: string): Promise<boolean> {
    try {
      // 1. 删除图书信息
      await this.storage.deleteBookInfo(bookId);

      // 2. 删除用户数据
      await this.storage.deleteUserBookData(bookId);

      // 3. 清理缓存
      this.bookCache.delete(bookId);

      return true;

    } catch (error) {
      console.error('Remove book from library failed:', error);
      return false;
    }
  }

  async getLibraryBooks(limit?: number, offset?: number): Promise<BookInfo[]> {
    try {
      const allBooks = await this.storage.getAllBookInfo();
      
      // 按添加时间排序（最新的在前）
      const sortedBooks = allBooks.sort((a, b) => {
        const aTime = a.updateTime?.getTime() || 0;
        const bTime = b.updateTime?.getTime() || 0;
        return bTime - aTime;
      });

      // 分页处理
      if (limit !== undefined) {
        const start = offset || 0;
        return sortedBooks.slice(start, start + limit);
      }

      return sortedBooks;

    } catch (error) {
      console.error('Get library books failed:', error);
      return [];
    }
  }

  async isBookInLibrary(bookId: string): Promise<boolean> {
    try {
      const bookInfo = await this.storage.getBookInfo(bookId);
      return bookInfo !== null;
    } catch (error) {
      console.error('Check book in library failed:', error);
      return false;
    }
  }

  // ============================================================================
  // 收藏和标签管理
  // ============================================================================

  async toggleBookFavorite(bookId: string): Promise<boolean> {
    try {
      const userData = await this.storage.getUserBookData(bookId);
      if (!userData) {
        return false;
      }

      userData.isFavorite = !userData.isFavorite;
      await this.storage.saveUserBookData(userData);

      return userData.isFavorite;

    } catch (error) {
      console.error('Toggle book favorite failed:', error);
      return false;
    }
  }

  async addBookTag(bookId: string, tag: string): Promise<boolean> {
    try {
      const userData = await this.storage.getUserBookData(bookId);
      if (!userData) {
        return false;
      }

      if (!userData.tags.includes(tag)) {
        userData.tags.push(tag);
        await this.storage.saveUserBookData(userData);
      }

      return true;

    } catch (error) {
      console.error('Add book tag failed:', error);
      return false;
    }
  }

  async removeBookTag(bookId: string, tag: string): Promise<boolean> {
    try {
      const userData = await this.storage.getUserBookData(bookId);
      if (!userData) {
        return false;
      }

      userData.tags = userData.tags.filter(t => t !== tag);
      await this.storage.saveUserBookData(userData);

      return true;

    } catch (error) {
      console.error('Remove book tag failed:', error);
      return false;
    }
  }

  async getFavoriteBooks(): Promise<BookInfo[]> {
    try {
      const allUserData = await this.storage.getAllUserBookData();
      const favoriteBookIds = allUserData
        .filter(data => data.isFavorite)
        .map(data => data.bookId);

      const favoriteBooks: BookInfo[] = [];
      
      for (const bookId of favoriteBookIds) {
        const bookInfo = await this.storage.getBookInfo(bookId);
        if (bookInfo) {
          favoriteBooks.push(bookInfo);
        }
      }

      return favoriteBooks;

    } catch (error) {
      console.error('Get favorite books failed:', error);
      return [];
    }
  }

  async getBooksByTag(tag: string): Promise<BookInfo[]> {
    try {
      const allUserData = await this.storage.getAllUserBookData();
      const taggedBookIds = allUserData
        .filter(data => data.tags.includes(tag))
        .map(data => data.bookId);

      const taggedBooks: BookInfo[] = [];
      
      for (const bookId of taggedBookIds) {
        const bookInfo = await this.storage.getBookInfo(bookId);
        if (bookInfo) {
          taggedBooks.push(bookInfo);
        }
      }

      return taggedBooks;

    } catch (error) {
      console.error('Get books by tag failed:', error);
      return [];
    }
  }

  async getAllTags(): Promise<string[]> {
    try {
      const allUserData = await this.storage.getAllUserBookData();
      const allTags = new Set<string>();

      allUserData.forEach(data => {
        data.tags.forEach(tag => allTags.add(tag));
      });

      return Array.from(allTags).sort();

    } catch (error) {
      console.error('Get all tags failed:', error);
      return [];
    }
  }

  // ============================================================================
  // 评分管理
  // ============================================================================

  async rateBook(bookId: string, rating: number, review?: string): Promise<boolean> {
    try {
      if (rating < 1 || rating > 5) {
        throw new Error('Rating must be between 1 and 5');
      }

      const userData = await this.storage.getUserBookData(bookId);
      if (!userData) {
        return false;
      }

      const bookRating: BookRating = {
        bookId,
        rating,
        review,
        ratedTime: new Date()
      };

      userData.rating = bookRating;
      await this.storage.saveUserBookData(userData);

      return true;

    } catch (error) {
      console.error('Rate book failed:', error);
      return false;
    }
  }

  async getBookRating(bookId: string): Promise<BookRating | null> {
    try {
      const userData = await this.storage.getUserBookData(bookId);
      return userData?.rating || null;
    } catch (error) {
      console.error('Get book rating failed:', error);
      return null;
    }
  }

  async getRatedBooks(): Promise<Array<{ book: BookInfo; rating: BookRating }>> {
    try {
      const allUserData = await this.storage.getAllUserBookData();
      const ratedData = allUserData.filter(data => data.rating);

      const ratedBooks: Array<{ book: BookInfo; rating: BookRating }> = [];

      for (const userData of ratedData) {
        const bookInfo = await this.storage.getBookInfo(userData.bookId);
        if (bookInfo && userData.rating) {
          ratedBooks.push({
            book: bookInfo,
            rating: userData.rating
          });
        }
      }

      // 按评分时间排序
      return ratedBooks.sort((a, b) => 
        b.rating.ratedTime.getTime() - a.rating.ratedTime.getTime()
      );

    } catch (error) {
      console.error('Get rated books failed:', error);
      return [];
    }
  }

  // ============================================================================
  // 重复图书检测和合并
  // ============================================================================

  async findDuplicateBooks(): Promise<Array<{ original: BookInfo; duplicates: BookInfo[] }>> {
    try {
      const allBooks = await this.storage.getAllBookInfo();
      const duplicateGroups: Array<{ original: BookInfo; duplicates: BookInfo[] }> = [];
      const processedBooks = new Set<string>();

      for (const book of allBooks) {
        if (processedBooks.has(book.id)) {
          continue;
        }

        const duplicates = allBooks.filter(otherBook => 
          otherBook.id !== book.id &&
          !processedBooks.has(otherBook.id) &&
          this.areBooksEqual(book, otherBook)
        );

        if (duplicates.length > 0) {
          duplicateGroups.push({
            original: book,
            duplicates
          });

          // 标记为已处理
          processedBooks.add(book.id);
          duplicates.forEach(dup => processedBooks.add(dup.id));
        }
      }

      return duplicateGroups;

    } catch (error) {
      console.error('Find duplicate books failed:', error);
      return [];
    }
  }

  private areBooksEqual(book1: BookInfo, book2: BookInfo): boolean {
    // 标题和作者相同认为是重复图书
    const title1 = this.normalizeTitle(book1.title);
    const title2 = this.normalizeTitle(book2.title);
    const author1 = this.normalizeAuthor(book1.author);
    const author2 = this.normalizeAuthor(book2.author);

    return title1 === title2 && author1 === author2;
  }

  private normalizeTitle(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^\w\s\u4e00-\u9fff]/g, '') // 保留字母、数字、空格和中文
      .replace(/\s+/g, ' ')
      .trim();
  }

  private normalizeAuthor(author: string): string {
    return author
      .toLowerCase()
      .replace(/[^\w\s\u4e00-\u9fff]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  async mergeDuplicateBooks(originalId: string, duplicateIds: string[]): Promise<boolean> {
    try {
      const originalBook = await this.storage.getBookInfo(originalId);
      if (!originalBook) {
        return false;
      }

      // 合并图书信息
      for (const duplicateId of duplicateIds) {
        const duplicateBook = await this.storage.getBookInfo(duplicateId);
        if (duplicateBook) {
          // 合并章节列表（选择更完整的）
          if (duplicateBook.chapters.length > originalBook.chapters.length) {
            originalBook.chapters = duplicateBook.chapters;
          }

          // 合并其他信息
          if (!originalBook.coverUrl && duplicateBook.coverUrl) {
            originalBook.coverUrl = duplicateBook.coverUrl;
          }
          if (!originalBook.description && duplicateBook.description) {
            originalBook.description = duplicateBook.description;
          }

          // 合并标签
          const allTags = new Set([...originalBook.tags, ...duplicateBook.tags]);
          originalBook.tags = Array.from(allTags);

          // 删除重复图书
          await this.removeBookFromLibrary(duplicateId);
        }
      }

      // 更新原始图书
      await this.storage.saveBookInfo(originalBook);

      return true;

    } catch (error) {
      console.error('Merge duplicate books failed:', error);
      return false;
    }
  }

  // ============================================================================
  // 图书集合管理
  // ============================================================================

  async createBookCollection(name: string, description?: string): Promise<string> {
    try {
      const collection: BookCollection = {
        id: this.generateCollectionId(),
        name,
        description,
        bookIds: [],
        createdTime: new Date(),
        updatedTime: new Date()
      };

      await this.storage.saveBookCollection(collection);
      return collection.id;

    } catch (error) {
      console.error('Create book collection failed:', error);
      throw error;
    }
  }

  async addBookToCollection(collectionId: string, bookId: string): Promise<boolean> {
    try {
      const collection = await this.storage.getBookCollection(collectionId);
      if (!collection) {
        return false;
      }

      if (!collection.bookIds.includes(bookId)) {
        collection.bookIds.push(bookId);
        collection.updatedTime = new Date();
        await this.storage.saveBookCollection(collection);
      }

      return true;

    } catch (error) {
      console.error('Add book to collection failed:', error);
      return false;
    }
  }

  async removeBookFromCollection(collectionId: string, bookId: string): Promise<boolean> {
    try {
      const collection = await this.storage.getBookCollection(collectionId);
      if (!collection) {
        return false;
      }

      collection.bookIds = collection.bookIds.filter(id => id !== bookId);
      collection.updatedTime = new Date();
      await this.storage.saveBookCollection(collection);

      return true;

    } catch (error) {
      console.error('Remove book from collection failed:', error);
      return false;
    }
  }

  async getBookCollections(): Promise<BookCollection[]> {
    try {
      return await this.storage.getAllBookCollections();
    } catch (error) {
      console.error('Get book collections failed:', error);
      return [];
    }
  }

  async getCollectionBooks(collectionId: string): Promise<BookInfo[]> {
    try {
      const collection = await this.storage.getBookCollection(collectionId);
      if (!collection) {
        return [];
      }

      const books: BookInfo[] = [];
      
      for (const bookId of collection.bookIds) {
        const bookInfo = await this.storage.getBookInfo(bookId);
        if (bookInfo) {
          books.push(bookInfo);
        }
      }

      return books;

    } catch (error) {
      console.error('Get collection books failed:', error);
      return [];
    }
  }

  // ============================================================================
  // 工具方法
  // ============================================================================

  private generateCollectionId(): string {
    return `collection_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  async getLibraryStats(): Promise<{
    totalBooks: number;
    favoriteBooks: number;
    ratedBooks: number;
    totalTags: number;
    collections: number;
  }> {
    try {
      const [allBooks, allUserData, allCollections] = await Promise.all([
        this.storage.getAllBookInfo(),
        this.storage.getAllUserBookData(),
        this.storage.getAllBookCollections()
      ]);

      const favoriteCount = allUserData.filter(data => data.isFavorite).length;
      const ratedCount = allUserData.filter(data => data.rating).length;
      
      const allTags = new Set<string>();
      allUserData.forEach(data => {
        data.tags.forEach(tag => allTags.add(tag));
      });

      return {
        totalBooks: allBooks.length,
        favoriteBooks: favoriteCount,
        ratedBooks: ratedCount,
        totalTags: allTags.size,
        collections: allCollections.length
      };

    } catch (error) {
      console.error('Get library stats failed:', error);
      return {
        totalBooks: 0,
        favoriteBooks: 0,
        ratedBooks: 0,
        totalTags: 0,
        collections: 0
      };
    }
  }

  clearCache(): void {
    this.bookCache.clear();
  }
}